import { CheckIcon, ChevronDown, CirclePlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function CommandAddItem({ query, onCreate }: { query: string; onCreate: () => void }) {
  return (
    <div
      tabIndex={0}
      onClick={onCreate}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
          onCreate();
        }
      }}
      className={cn(
        "flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-blue-500 focus:outline-none",
        "hover:bg-blue-200 focus:!bg-blue-200",
      )}
    >
      <CirclePlus className="mr-2 h-4 w-4" />
      Create "{query}"
    </div>
  );
}

export type ComboboxOption = {
  value: string;
  label: string;
};

type BaseProps = {
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  modal?: boolean;
  onCreate?: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
};

type SingleComboboxProps = BaseProps & {
  multiple?: false;
  value?: string | null;
  onChange: (value: string) => void;
};

type MultiComboboxProps = BaseProps & {
  multiple: true;
  value?: string[];
  onChange: (value: string[]) => void;
};

type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

export const Combobox = ({
  className,
  disabled = false,
  fullWidth = false,
  modal = false,
  multiple = false,
  onChange,
  onCreate,
  options,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  value,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [canCreate, setCanCreate] = useState(false);

  useEffect(() => {
    if (!onCreate) {
      setCanCreate(false);
      return;
    }

    const exists = options.some(
      (option) => option.label.toLowerCase() === query.trim().toLowerCase(),
    );

    setCanCreate(Boolean(query.trim()) && !exists);
  }, [query, options, onCreate]);

  const selectedValues = useMemo(
    () => (multiple ? (value ?? []) : value ? [value] : []),
    [multiple, value],
  );

  const selectedLabels = useMemo(
    () =>
      options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.label),
    [options, selectedValues],
  );

  function handleSelect(optionValue: string) {
    if (!multiple) {
      (onChange as (value: string) => void)(optionValue);
      setOpen(false);
      setQuery("");
      return;
    }

    const current = selectedValues as string[];

    if (current.includes(optionValue)) {
      (onChange as (value: string[]) => void)(current.filter((v) => v !== optionValue));
    } else {
      (onChange as (value: string[]) => void)([...current, optionValue]);
    }
  }

  function handleCreate() {
    if (!(onCreate && query.trim())) {
      return;
    }

    onCreate(query.trim());

    if (multiple) {
      const current = selectedValues as string[];
      (onChange as (value: string[]) => void)([...current, query.trim()]);
    } else {
      (onChange as (value: string) => void)(query.trim());
      setOpen(false);
    }

    setQuery("");
  }

  const triggerText = (() => {
    if (multiple) {
      if (selectedLabels.length === 0) {
        return placeholder;
      }

      if (selectedLabels.length <= 2) {
        return selectedLabels.join(", ");
      }

      return `${selectedLabels.length} selected`;
    }

    return value !== null && value !== undefined
      ? options.find((option) => option.value === value)?.label
      : placeholder;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-fit justify-between", fullWidth && "w-full", className)}
        >
          {triggerText}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          "w-fit p-0",
          fullWidth && "w-[var(--radix-popover-trigger-width)]",
          className,
        )}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
          />

          <CommandList>
            {canCreate && <CommandAddItem query={query} onCreate={handleCreate} />}
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup>
              {options.length === 0 && canCreate && !query && (
                <div className="space-y-1 py-1.5 pl-8 text-sm">
                  <p>No results.</p>
                  <p>Enter a value to create a new option.</p>
                </div>
              )}

              {options.map((option) => {
                const selected = selectedValues.includes(option.value);

                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={() => handleSelect(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.stopPropagation();
                        handleSelect(option.value);
                      }
                    }}
                  >
                    <CheckIcon
                      className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                    />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
